import { IImportCreate, IImportPayload } from 'bom/types';
import { mapRecordFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetOneResponse } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';
import { VendorQuoteAttachToType } from 'quotes/enums';
import { omitVendorMappingFromImportPayload } from 'quotes/mappers';
import {
  IExtractDataProgress,
  IImportStatus,
  IImportVendorQuotePayload,
  IVendorQuote,
  IVendorQuoteImport,
} from 'quotes/types';

export const validateVendorQuoteBeforeImport = async (
  requestPayload: IImportVendorQuotePayload,
  signal?: AbortSignal
): Promise<GetOneResponse<IVendorQuote>> => {
  const { data } = await partfinitiClient.get(
    // Strip null/undefined so an absent revision/external number is omitted rather than serialized
    // as the literal "null"/"undefined"; the BE then reads it as absent and dedupes with IS NULL.
    `/quote/import/validate-vendor-quote-before-import?${getQueryStringToSnakeCase(
      omitVendorMappingFromImportPayload(requestPayload),
      { removeNullValues: true }
    )}`,
    signal
  );

  return mapRecordFromServer<IVendorQuote>(data.quote);
};

export const createVendorQuoteImport = async (data: {
  file: number;
}): Promise<IVendorQuoteImport> =>
  (await partfinitiClient.postForm('/quote/import/create-vendor-quote-import', data)).import;

export const extractDataProgress = async (
  requestPayload: IImportPayload,
  signal?: AbortSignal
): Promise<IExtractDataProgress> => {
  const { data } = await partfinitiClient.get(
    `/quote/import/extract-data-progress?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data);
};

export const importStatus = async (
  requestPayload: IImportPayload,
  signal?: AbortSignal
): Promise<IImportStatus> => {
  const { data } = await partfinitiClient.get(
    `/import/import/status?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data);
};

export const importQuoteToQuote = (data: IImportVendorQuotePayload) =>
  partfinitiClient.postForm('/quote/quote-association-edit/import', data);

export const importQuoteToProject = (data: IImportVendorQuotePayload) =>
  partfinitiClient.postForm('/project_v2/quote-association-edit/import', data);

export const importQuoteToJob = (data: IImportVendorQuotePayload) => {
  const { quote, ...rest } = data;
  const attachTo = quote?.attachTo === VendorQuoteAttachToType.JOB ? 'bom' : quote.attachTo;

  return partfinitiClient.postForm('/quote/import/start', {
    quote: {
      ...quote,
      attachTo,
    },
    ...rest,
  });
};

export const createQuoteImport = (
  data: { quote: number; file: number },
  signal?: AbortSignal
): Promise<IImportCreate> => partfinitiClient.postForm('/quote/import/create', data, { signal });
